'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/dialog/confirm/styles.css';
import { Button } from 'BomdisoftComponents/buttons/index';
import { Dialog, DialogActions, DialogContent, DialogTitle } from 'BomdisoftComponents/dialog/index';
import { TextField } from 'BomdisoftComponents/inputs/field/text';
import { cancel, type SupportedLanguage } from '@bomdisoft/translations';
import { type ChangeEvent, type ReactNode, useState } from 'react';

export type ConfirmDialogProps = {
  cancelLabel?: ReactNode | false;
  confirmLabel: ReactNode;
  confirmPhrase?: string;
  confirmPhraseLabel?: string;
  description?: ReactNode;
  destructive?: boolean;
  dismissible?: boolean;
  icon?: ReactNode;
  language: SupportedLanguage;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  pending?: boolean;
  testId?: string;
  title: ReactNode;
};

export const ConfirmDialog = ({ cancelLabel, confirmLabel, confirmPhrase, confirmPhraseLabel, description, destructive, dismissible, icon, language, onClose, onConfirm, open, pending, testId, title }: ConfirmDialogProps) => {
  const [typed, setTyped] = useState('');
  const unmatched = confirmPhrase !== undefined && typed.trim().toLowerCase() !== confirmPhrase.trim().toLowerCase();

  const handleClose = () => {
    setTyped('');
    onClose();
  };

  return (
    <Dialog dismissible={dismissible} form={confirmPhrase !== undefined} onClose={handleClose} open={open} testId={testId}>
      <DialogTitle>
        {icon === undefined ? (
          title
        ) : (
          <span className='bomdisoft-confirm-dialog-head-row'>
            <span className='bomdisoft-confirm-dialog-glyph'>{icon}</span>
            {title}
          </span>
        )}
      </DialogTitle>
      <DialogContent>
        {description === undefined ? null : <div className='bomdisoft-confirm-dialog-description'>{description}</div>}
        {confirmPhrase === undefined ? null : <TextField autoFocus label={confirmPhraseLabel} onChange={(event: ChangeEvent<HTMLInputElement>) => setTyped(event.target.value)} placeholder={confirmPhrase} type='text' value={typed} />}
      </DialogContent>
      <DialogActions>
        {cancelLabel === false ? null : (
          <Button color='neutral' data-testid={testId ? `${testId}-cancel` : undefined} onClick={handleClose} variant='text'>
            {cancelLabel ?? cancel[language]}
          </Button>
        )}
        <Button color={destructive ? 'error' : undefined} data-testid={testId ? `${testId}-confirm` : undefined} disabled={unmatched || pending === true} onClick={onConfirm} variant='contained'>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
